/**
 * ============================================================================
 * Recommendation Details Drawer (Level 2)
 * ============================================================================
 *
 * Displays detailed action plan for a recommendation when user clicks
 * "Voir le détail" button.
 *
 * Features:
 * - Fetches detailed explanation via tRPC recommendations.explain
 * - Shows: Why, Steps, Effort, Stakeholders, Timeline
 * - Loading and error states with graceful fallback
 * - Never crashes - always displays something useful
 *
 * ============================================================================
 */

import { trpc } from "@/lib/trpc";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Loader2,
  AlertCircle,
  CheckCircle2,
  Users,
  Clock,
  Zap,
  ListOrdered,
} from "lucide-react";

// ============================================================================
// TYPES
// ============================================================================

interface RecommendationDetailsDrawerProps {
  recommendationId: string | null;
  recommendationTitle?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface RecommendationDetails {
  why: string;
  steps: string[];
  effort: "low" | "medium" | "high";
  stakeholders: string[];
  timeline: "short" | "medium" | "long";
  cached?: boolean;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const EFFORT_CONFIG: Record<
  string,
  { label: string; color: string; icon: typeof Zap }
> = {
  low: {
    label: "Effort faible",
    color: "bg-green-100 text-green-800 dark:bg-green-950/20 dark:text-green-400",
    icon: Zap,
  },
  medium: {
    label: "Effort modéré",
    color: "bg-yellow-100 text-yellow-800 dark:bg-yellow-950/20 dark:text-yellow-400",
    icon: Zap,
  },
  high: {
    label: "Effort important",
    color: "bg-red-100 text-red-800 dark:bg-red-950/20 dark:text-red-400",
    icon: Zap,
  },
};

const TIMELINE_CONFIG: Record<
  string,
  { label: string; color: string; icon: typeof Clock }
> = {
  short: {
    label: "Court terme (< 6 mois)",
    color: "bg-blue-100 text-blue-800 dark:bg-blue-950/20 dark:text-blue-400",
    icon: Clock,
  },
  medium: {
    label: "Moyen terme (6-18 mois)",
    color: "bg-purple-100 text-purple-800 dark:bg-purple-950/20 dark:text-purple-400",
    icon: Clock,
  },
  long: {
    label: "Long terme (> 18 mois)",
    color: "bg-orange-100 text-orange-800 dark:bg-orange-950/20 dark:text-orange-400",
    icon: Clock,
  },
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function RecommendationDetailsDrawer({
  recommendationId,
  recommendationTitle,
  open,
  onOpenChange,
}: RecommendationDetailsDrawerProps) {
  // Query details only when drawer is open and ID is provided
  const detailsQuery = trpc.recommendations.explain.useQuery(
    { id: recommendationId! },
    {
      enabled: open && !!recommendationId,
      retry: 1,
      staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    }
  );

  const details = detailsQuery.data as RecommendationDetails | undefined;
  const isLoading = detailsQuery.isLoading;
  const isError = detailsQuery.isError;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="text-xl font-semibold">
            {recommendationTitle || "Plan d'action détaillé"}
          </SheetTitle>
          <SheetDescription>
            Découvrez comment mettre en œuvre cette recommandation de manière
            opérationnelle.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {/* Loading State */}
          {isLoading && (
            <div className="flex flex-col items-center justify-center py-12 space-y-4">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">
                Génération du plan d'action...
              </p>
            </div>
          )}

          {/* Error State */}
          {isError && !isLoading && (
            <Card className="border-orange-200 bg-orange-50 dark:bg-orange-950/20">
              <CardContent className="pt-6">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="h-5 w-5 text-orange-600 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="font-semibold text-orange-900 dark:text-orange-100">
                      Détails temporairement indisponibles
                    </h3>
                    <p className="text-sm text-orange-700 dark:text-orange-300 mt-1">
                      Impossible de charger le plan d'action détaillé pour le
                      moment. Veuillez réessayer dans quelques instants.
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => detailsQuery.refetch()}
                      className="mt-3"
                    >
                      Réessayer
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Success State - Display Details */}
          {details && !isLoading && (
            <>
              {/* Why Section */}
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-start space-x-3">
                    <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <h3 className="font-semibold text-foreground mb-2">
                        Pourquoi cette action ?
                      </h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {details.why}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Steps Section */}
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-start space-x-3">
                    <ListOrdered className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <h3 className="font-semibold text-foreground mb-3">
                        Étapes de mise en œuvre
                      </h3>
                      <ol className="space-y-3">
                        {details.steps.map((step, index) => (
                          <li key={index} className="flex items-start space-x-3">
                            <span className="flex-shrink-0 flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-semibold">
                              {index + 1}
                            </span>
                            <span className="text-sm text-muted-foreground flex-1 pt-0.5">
                              {step}
                            </span>
                          </li>
                        ))}
                      </ol>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Effort & Timeline Row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Effort */}
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-start space-x-3">
                      <Zap className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <h3 className="font-semibold text-foreground mb-2">
                          Effort requis
                        </h3>
                        <Badge
                          variant="secondary"
                          className={EFFORT_CONFIG[details.effort]?.color}
                        >
                          {EFFORT_CONFIG[details.effort]?.label || details.effort}
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Timeline */}
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-start space-x-3">
                      <Clock className="h-5 w-5 text-purple-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <h3 className="font-semibold text-foreground mb-2">
                          Délai de mise en œuvre
                        </h3>
                        <Badge
                          variant="secondary"
                          className={TIMELINE_CONFIG[details.timeline]?.color}
                        >
                          {TIMELINE_CONFIG[details.timeline]?.label || details.timeline}
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Stakeholders Section */}
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-start space-x-3">
                    <Users className="h-5 w-5 text-indigo-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <h3 className="font-semibold text-foreground mb-3">
                        Acteurs impliqués
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {details.stakeholders.map((stakeholder, index) => (
                          <Badge
                            key={index}
                            variant="outline"
                            className="text-xs"
                          >
                            {stakeholder}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Cache indicator (optional) */}
              {details.cached && (
                <p className="text-xs text-muted-foreground text-center">
                  ℹ️ Ces détails ont été générés précédemment
                </p>
              )}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
